const store = {
    namespaced: true,
    state: () => ({
        cities: [],
        streets: [],
        warehouses: [],
        postMachines: [],
        deliveryTypes: [],
        activeCity: null,
        activeStreet: null,
        activeWarehouse: null,
        activePostMachine: null,
        activeHouseNumber: null,
        activeEntrance: null,
        activeApartment: null,
        isCitiesLoading: false,
        isStreetsLoading: false,
        isDeliveryLoading: false,
        isWarehousesLoading: false,
        isPostMachinesLoading: false,
        activeDeliveryType: null,
        isNovaPoshtaSelected: false,
        novaPoshtaDeliveryData: null,
        novaPoshtaWarningText: null,
    }),

    getters: {
        getCities: state => state.cities,
        getStreets: state => {
            return state.streets?.map(street => {
                const type = street.streetType || 'str';
                return {
                    ...street,
                    description: `${type}. ${street.description || ''}`,
                    descriptionRo: `${type}. ${street.descriptionRo || ''}`,
                };
            });
        },
        getWarehouses: state => {
            return state.warehouses?.map(warehouse => ({
                ref: warehouse?.ref,
                description: warehouse?.address?.description || '',
            }));
        },
        getPostMachines: state => {
            return state.postMachines?.map(postMachines => ({
                ref: postMachines?.ref,
                description: postMachines?.address?.description || '',
            }));
        },
        getActiveHouseNumber: state => state.activeHouseNumber,
        getActiveEntrance: state => state.activeEntrance,
        getActiveApartment: state => state.activeApartment,
        getDeliveryTypes: state => state.deliveryTypes,
        getNovaPoshtaIsActive: state => {
            return state.deliveryTypes?.length > 0;
        },
        getActiveCity: state => state.activeCity,
        getActiveStreet: state => state.activeStreet,
        getActiveWarehouse: state => state.activeWarehouse,
        getActivePostMachine: state => state.activePostMachine,
        getIsCitiesLoading: state => state.isCitiesLoading,
        getIsStreetsLoading: state => state.isStreetsLoading,
        getIsWarehousesLoading: state => state.isWarehousesLoading,
        getIsPostMachinesLoading: state => state.isPostMachinesLoading,
        getActiveDeliveryType: state => state.activeDeliveryType,
        getIsNovaPoshtaSelected: state => state.isNovaPoshtaSelected,
        getNovaPoshtaOrderData: state => {
            const data = {
                type: state.activeDeliveryType,
                city_ref_id: state.activeCity?.ref,
            };

            if (state.activeDeliveryType === 'courier') {
                data.address = {
                    ref: state.activeStreet?.ref,
                    description: state.activeStreet?.description,
                    descriptionRo: state.activeStreet?.descriptionRo,
                    streetType: state.activeStreet?.streetType,
                    house_number: state.activeHouseNumber,
                    entrance: state.activeEntrance,
                    apartment: state.activeApartment,
                };
            }

            if (state.activeDeliveryType === 'warehouse') {
                data.warehouse_ref_id = state.activeWarehouse?.ref;
            }

            if (state.activeDeliveryType === 'postmachine') {
                data.postmachine_ref_id = state.activePostMachine?.ref;
            }

            return data;
        },
        getNovaPoshtaDeliveryData: state => {
            return state.novaPoshtaDeliveryData;
        },
        getNovaPoshtaWarningText: state => {
            return state.novaPoshtaWarningText;
        },
        getIsDeliveryLoading: state => {
            return state.isDeliveryLoading;
        },
    },

    mutations: {
        SET_CITIES(state, payload) {
            state.cities = payload;
        },
        SET_STREETS(state, payload) {
            state.streets = payload;
        },
        SET_WAREHOUSES(state, payload) {
            state.warehouses = payload;
        },
        SET_POST_MACHINES(state, payload) {
            state.postMachines = payload;
        },
        SET_DELIVERY_TYPES(state, payload) {
            state.deliveryTypes = payload;
        },
        SET_ACTIVE_CITY(state, payload) {
            state.activeCity = payload;
        },
        SET_ACTIVE_STREET(state, payload) {
            state.activeStreet = payload;
        },
        SET_ACTIVE_WAREHOUSE(state, payload) {
            state.activeWarehouse = payload;
        },
        SET_ACTIVE_HOUSE_NUMBER(state, payload) {
            state.activeHouseNumber = payload;
        },
        SET_ACTIVE_ENTRANCE(state, payload) {
            state.activeEntrance = payload;
        },
        SET_ACTIVE_APARTMENT(state, payload) {
            state.activeApartment = payload;
        },
        SET_ACTIVE_POST_MACHINE(state, payload) {
            state.activePostMachine = payload;
        },
        SET_IS_CITIES_LOADING(state, payload) {
            state.isCitiesLoading = payload;
        },
        SET_IS_STREETS_LOADING(state, payload) {
            state.isStreetsLoading = payload;
        },
        SET_IS_WAREHOUSES_LOADING(state, payload) {
            state.isWarehousesLoading = payload;
        },
        SET_IS_POST_MACHINES_LOADING(state, payload) {
            state.isPostMachinesLoading = payload;
        },
        SET_ACTIVE_DELIVERY_TYPE(state, payload) {
            state.activeDeliveryType = payload;
        },
        SET_IS_NOVA_POSHTA_SELECTED(state, payload) {
            state.isNovaPoshtaSelected = payload;
        },
        SET_NOVA_POSHTA_DELIVERY_DATA(state, payload) {
            state.novaPoshtaDeliveryData = payload;
        },
        SET_NOVA_POSHTA_WARNING_TEXT(state, payload) {
            state.novaPoshtaWarningText = payload;
        },
        SET_IS_DELIVERY_LOADING(state, payload) {
            state.isDeliveryLoading = payload;
        },
    },

    actions: {
        async fetchData({ commit }, { url, loadingMutation, dataMutation, payload }) {
            commit(loadingMutation, true);
            try {
                commit(dataMutation, []);
                const { data } = await this.$axios.$post(url, payload);
                commit(dataMutation, data);
            } catch (error) {
                console.error(`${url} error:`, error);
            } finally {
                commit(loadingMutation, false);
            }
        },
        async fetchCities({ commit, dispatch }, query = '') {
            return dispatch('fetchData', {
                url: '/general/v2/novapost/city/',
                loadingMutation: 'SET_IS_CITIES_LOADING',
                dataMutation: 'SET_CITIES',
                payload: { query },
            });
        },

        async fetchStreets({ commit, dispatch }, { cityRef, query }) {
            return dispatch('fetchData', {
                url: '/general/v2/novapost/street/',
                loadingMutation: 'SET_IS_STREETS_LOADING',
                dataMutation: 'SET_STREETS',
                payload: { cityRef, query },
            });
        },

        async fetchWarehouses({ commit, dispatch }, { cityRef, number }) {
            return dispatch('fetchData', {
                url: '/general/v2/novapost/warehouse/',
                loadingMutation: 'SET_IS_WAREHOUSES_LOADING',
                dataMutation: 'SET_WAREHOUSES',
                payload: {
                    cityRef,
                    number: Number(number) || undefined,
                },
            });
        },

        async fetchPostMachines({ commit, dispatch }, { cityRef, number }) {
            return dispatch('fetchData', {
                url: '/general/v2/novapost/postmachine/',
                loadingMutation: 'SET_IS_POST_MACHINES_LOADING',
                dataMutation: 'SET_POST_MACHINES',
                payload: {
                    cityRef,
                    number: Number(number) || undefined,
                },
            });
        },
        async fetchDeliveryPrice({ commit }, payload) {
            // commit('SET_NOVA_POSHTA_DELIVERY_DATA', null);
            commit('SET_NOVA_POSHTA_WARNING_TEXT', null);
            commit('SET_IS_DELIVERY_LOADING', true);
            try {
                const result = await this.$axios.$post('/general/v2/novapost/delivery-price/', payload);
                const value = result[0];

                if (typeof value === 'object' && value !== null) {
                    commit('SET_NOVA_POSHTA_DELIVERY_DATA', value);
                    commit('SET_NOVA_POSHTA_WARNING_TEXT', null);
                }

                if (typeof value === 'string') {
                    commit('SET_NOVA_POSHTA_DELIVERY_DATA', null);
                    commit('SET_NOVA_POSHTA_WARNING_TEXT', value);
                }
            } catch (error) {
                console.error('fetchDeliveryPrice error:', error);
                throw error;
            } finally {
                commit('SET_IS_DELIVERY_LOADING', false);
            }
        },
    },
};

export default store;
